import type {
  TenderState,
  SpoilReconciliationData,
  FoulBuildUpRowData,
  SewerPipeRun,
  LateralConnection,
  StormSewerRun,
  GullyConnection,
  PumpChamberConfig,
  PrelimsItemRecord,
} from "../types/tender.types";
import { importFoulSewersFromExcel } from "./foulSewersExcelUtils";
import { importStormSewersFromExcel } from "./stormSewersExcelUtils";
import { DEFAULT_POND_SECTIONS, MAX_POND_SECTIONS } from "./stormBuildUpPonds";
import { DEFAULT_SWALE_SECTIONS, MAX_SWALE_SECTIONS } from "./stormBuildUpSwales";
import { DEFAULT_CELLULAR_SECTIONS, MAX_CELLULAR_SECTIONS } from "./stormBuildUpCellularAttenuation";
import { DEFAULT_WEHOLITE_SECTIONS, MAX_WEHOLITE_SECTIONS } from "./stormBuildUpWeholiteAttenuation";
import { DEFAULT_CONC_SECTIONS, MAX_CONC_SECTIONS } from "./stormBuildUpConcAttenuation";
import { DEFAULT_BOX_CULVERT_SECTIONS, MAX_BOX_CULVERT_SECTIONS } from "./stormBuildUpBoxCulverts";
import { parsePOSFromExcel } from "./posExcelUtils";
import { parseCompoundFromExcel } from "./compoundExcelUtils";
import { parseSubstationBaseFromExcel } from "./substationBaseExcelUtils";
import { parseSpoilReconciliationFromExcel } from "./spoilReconciliationExcelUtils";
import {
  importRoadsFromExcel,
  parseCarriagewayTakeoffRoadTypes,
  parseFootpathsConfig,
  parseCarriagewayAndFootwaysData,
  parseCutFillSections,
  parseStripValue,
  parseKerbRaceWidth,
  parseRoadConstructionTypeSelections,
  parseMainServicesTrenching,
  parseCarriagewaySummaryManualQtys,
  parseCappingLayersFromExcel,
} from "./roadsExcelUtils";
import { calculateTotalConstructionThickness } from "./roadCalculations";
import { PRELIMS_MAPPING } from "./prelimsMapping";

export type RoadsAndSewersTakeoffCurrentState = Pick<
  TenderState,
  | "roads" | "foulSewers" | "pumpStation" | "risingMain" | "stormSewers"
  | "boxCulverts" | "cellularAttenuation" | "concAttenuation" | "pondsSwales"
  | "weholiteAttenuation" | "preliminaryItems" | "mainServices" | "pos"
  | "compound" | "substation" | "spoilReconciliation"
>;

export interface RoadsAndSewersTakeoffImportResult {
  updates: Partial<TenderState>;
  importedSheets: number;
  pageChanges: Array<{ page: string; count: number }>;
  /** Mirrors JobTypeSelection.tsx's post-import navigate condition: R&S takes priority only when one of these found data. */
  hasNavigationPriorityData: boolean;
}

/**
 * Parses a take-off Excel workbook for Roads & Sewers sheets and merges the
 * results into the current state slices. Extracted verbatim from
 * JobTypeSelection.tsx's performBulkImport (R&S branch) so the legacy
 * bulk-import modal and the /tender/import-takeoff page share one
 * implementation.
 */
export function importRoadsAndSewersTakeoff(
  file: File,
  current: RoadsAndSewersTakeoffCurrentState,
): Promise<RoadsAndSewersTakeoffImportResult> {
  return new Promise((resolve, reject) => {
    (async () => {
      try {
        let importedSheets = 0;

      // Run Roads & Sewers-specific imports in parallel so they're ready when the
      // FileReader callback fires. Rejected entries (e.g. missing sheets) are safe
      // — we check `.status === 'fulfilled'` before using the values.
      // Prelims uses inline workbook processing (see "Prelims" sheet handling below).
      const [
        roadsImportResult,
        foulImportResult,
        stormImportResult,
        carriagewayRoadTypesResult,
        footpathsConfigResult,
        carriagewayFootwaysResult,
        cutFillSectionsResult,
        stripValueResult,
        kerbRaceWidthResult,
        roadConstructionTypesResult,
        mainServicesTrenchingResult,
        posImportResult,
        compoundImportResult,
        substationBaseImportResult,
        spoilReconciliationImportResult,
        carriagewaySummaryManualQtysResult,
        cappingLayersResult,
      ] = await Promise.allSettled([
            importRoadsFromExcel(file),
            importFoulSewersFromExcel(file),
            importStormSewersFromExcel(file),
            parseCarriagewayTakeoffRoadTypes(file),
            parseFootpathsConfig(file),
            parseCarriagewayAndFootwaysData(file),
            parseCutFillSections(file),
            parseStripValue(file),
            parseKerbRaceWidth(file),
            parseRoadConstructionTypeSelections(file),
            parseMainServicesTrenching(file),
            parsePOSFromExcel(file),
            parseCompoundFromExcel(file),
            parseSubstationBaseFromExcel(file),
            parseSpoilReconciliationFromExcel(file),
            parseCarriagewaySummaryManualQtys(file),
            parseCappingLayersFromExcel(file),
      ]);

        const XLSX = await import("xlsx");
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target?.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: "array" });

          // ── Roads & Sewers import results ──────────────────────────────────
          // Process the parallel results that were awaited before this FileReader.
          let updatedRoads = current.roads;
          let updatedFoulSewers = current.foulSewers;
          let updatedPumpStation = current.pumpStation;
          let updatedRisingMain = current.risingMain;
          let updatedStormSewers = current.stormSewers;
          let updatedBoxCulverts = current.boxCulverts;
          let updatedCellularAttenuation = current.cellularAttenuation;
          let updatedConcAttenuation = current.concAttenuation;
          let updatedPondsSwales = current.pondsSwales;
          let updatedWeholiteAttenuation = current.weholiteAttenuation;
          let updatedPreliminaryItems = current.preliminaryItems;
          let updatedMainServices = current.mainServices;
          let roadsImported = false;
          let foulImported = false;
          let stormImported = false;
          let prelimsImported = false;
          let mainServicesImported = false;
          let posImported = false;
          let compoundImported = false;
          let substationBaseImported = false;
          let spoilReconciliationImported = false;

          // Resolve Carriageway Take Off parser results (same parsers as RoadsTakeoff handleUnifiedImport).
          const importedRoadTypes = carriagewayRoadTypesResult.status === 'fulfilled' ? carriagewayRoadTypesResult.value.roadTypes : [];
          const importedCappingType = carriagewayRoadTypesResult.status === 'fulfilled' ? carriagewayRoadTypesResult.value.cappingType : undefined;
          const importedFootpathsConfig = footpathsConfigResult.status === 'fulfilled' ? footpathsConfigResult.value : null;
          const importedCarriagewayFootways = carriagewayFootwaysResult.status === 'fulfilled' ? carriagewayFootwaysResult.value : [];
          const importedCutFillSections = cutFillSectionsResult.status === 'fulfilled' ? cutFillSectionsResult.value : [];
          const importedStripValue = stripValueResult.status === 'fulfilled' ? stripValueResult.value : null;
          const importedKerbRaceWidth = kerbRaceWidthResult.status === 'fulfilled' ? kerbRaceWidthResult.value : null;
          const importedRoadConstructionTypes = roadConstructionTypesResult.status === 'fulfilled'
            ? roadConstructionTypesResult.value
            : { tarmacConstructionTypeId: null, blockPavedConstructionTypeId: null };
          const importedMainServiceEntries = mainServicesTrenchingResult.status === 'fulfilled' ? mainServicesTrenchingResult.value : [];
          const importedManualQtyEntries = carriagewaySummaryManualQtysResult.status === 'fulfilled' ? carriagewaySummaryManualQtysResult.value : [];
          const importedCappingLayers = cappingLayersResult.status === 'fulfilled' ? cappingLayersResult.value : { thicknesses: [], t1ThicknessMm: {}, manualQtys: {} };

          if (roadsImportResult.status === 'fulfilled' && roadsImportResult.value) {
            const rd = roadsImportResult.value.data;
            // Carriageway Take Off data takes precedence over the generic roads import.
            // Carriageway Take Off's road-type parser doesn't compute totalConstructionThickness
            // (defaults to 0) — recompute it here the same way RoadsTakeoff.tsx does on mount,
            // so it isn't wrong until the user visits the Roads Takeoff page.
            const finalRoadTypes = (importedRoadTypes.length > 0 ? importedRoadTypes : rd.roadTypes).map(
              (roadType) => ({
                ...roadType,
                totalConstructionThickness: calculateTotalConstructionThickness(roadType),
              }),
            );
            const finalCarriagewayFootways = importedCarriagewayFootways.length > 0 ? importedCarriagewayFootways : rd.carriagewayFootways;
            const finalCutFillSections = importedCutFillSections.length > 0 ? importedCutFillSections : rd.cutFillSections;

            // Carriageway Summary manual-qty rows ("Filling with recycled 6F2 to
            // footpaths" / "Hot bond coat between layers") — match by road name,
            // fall back to column position (Road 1 = 1st road, etc.)
            const importedManualQtys: Record<string, number> = {};
            importedManualQtyEntries.forEach(({ key, roadIndex, roadHeaderName, quantity }) => {
              const byName = finalCarriagewayFootways.find(
                (r) => r.roadName.trim().toLowerCase() === roadHeaderName.toLowerCase(),
              );
              const road = byName ?? finalCarriagewayFootways[roadIndex];
              if (!road) return;
              importedManualQtys[`${key}-${road.id}`] = quantity;
            });
            // Merge in Capping Layers' own manual entries ("Add exc"/"Add 6F2 fill", keyed
            // by section thickness rather than road) — a separate object, not a road-keyed
            // one, so it's combined rather than overwriting the Carriageway Summary entries.
            Object.assign(importedManualQtys, importedCappingLayers.manualQtys);

            if (finalRoadTypes.length > 0 || finalCarriagewayFootways.length > 0 || finalCutFillSections.length > 0) {
              // Completely replace with imported data — do not preserve existing state.
              // `rd` is the flat RoadsExcelData DTO — re-nest into the RoadsData table/config shape.
              updatedRoads = {
                ...current.roads,
                roadTypes: { ...current.roads.roadTypes, table: finalRoadTypes },
                carriagewayFootways: {
                  ...current.roads.carriagewayFootways,
                  table: finalCarriagewayFootways,
                  config: {
                    ...current.roads.carriagewayFootways.config,
                    ...(importedKerbRaceWidth !== null ? { kerbRaceWidth: importedKerbRaceWidth } : {}),
                  },
                },
                cutFillSections: {
                  ...current.roads.cutFillSections,
                  table: finalCutFillSections,
                  config: {
                    ...current.roads.cutFillSections.config,
                    ...(importedStripValue !== null ? { stripValueDefault: importedStripValue } : {}),
                    ...(importedRoadConstructionTypes.tarmacConstructionTypeId !== null ? { tarmacConstructionTypeId: importedRoadConstructionTypes.tarmacConstructionTypeId } : {}),
                    ...(importedRoadConstructionTypes.blockPavedConstructionTypeId !== null ? { blockPavedConstructionTypeId: importedRoadConstructionTypes.blockPavedConstructionTypeId } : {}),
                  },
                },
                capping: {
                  ...current.roads.capping,
                  config: {
                    ...current.roads.capping.config,
                    ...(importedCappingLayers.thicknesses.length > 0
                      ? { cappingLayer: { enabled: true, thicknesses: importedCappingLayers.thicknesses, t1ThicknessMm: importedCappingLayers.t1ThicknessMm } }
                      : (rd.cappingLayer ? { cappingLayer: rd.cappingLayer } : {})),
                    ...(importedCappingType ? { cappingType: importedCappingType } : {}),
                  },
                },
                footpaths: {
                  ...current.roads.footpaths,
                  config: importedFootpathsConfig ?? current.roads.footpaths.config,
                },
                ...(Object.keys(importedManualQtys).length > 0 ? { manualQtys: importedManualQtys } : {}),
              };
              importedSheets++;
              roadsImported = true;
            }
          }

          if (foulImportResult.status === 'fulfilled' && foulImportResult.value) {
            const fd = foulImportResult.value.data;
            const hasBuildUp = fd.buildUp && Object.keys(fd.buildUp).length > 0;
            if (fd.manholes.length > 0 || fd.pipeRuns.length > 0 || fd.laterals.length > 0 || hasBuildUp || fd.pumpChamberConfig || fd.pumpStationSectionTitles) {
              // Completely replace with imported data — do not merge with existing records.
              // eoQtys is the exception: it holds the Foul Summary tab's manual-input-only
              // quantities (EO Concrete Bed & Surround, EO Items, Removal of Redundant
              // Sewer), matched by description text out of the 'Foul Summary' sheet if
              // present (see parseFoulSummarySheet) — merge rather than replace so a
              // workbook without that sheet, or with only some rows filled in, doesn't
              // wipe out previously-entered values. Same convention as
              // FoulSewersTakeoff.tsx's handleUnifiedImport.
              // Build-up rows are keyed 'ps_*' (Pump Station) / 'rm_*' (Rising Main) —
              // split by prefix into the two dedicated top-level state fields.
              const importedBuildUp = fd.buildUp || {};
              const importedPumpStationRows: Record<string, FoulBuildUpRowData> = {};
              const importedRisingMainRows: Record<string, FoulBuildUpRowData> = {};
              Object.entries(importedBuildUp).forEach(([id, row]) => {
                if (id.startsWith('ps_')) importedPumpStationRows[id] = row;
                else if (id.startsWith('rm_')) importedRisingMainRows[id] = row;
              });

              // The sheet's main-run and house-lateral sections are read independently
              // (not every run has a lateral), so the imported arrays commonly end up
              // different lengths. Pad the shorter one so every take-off row renders
              // with editable inputs on both sides, matching FoulSewersTakeoff.tsx's
              // handleUnifiedImport.
              const foulImportedRowCount = Math.max(fd.pipeRuns.length, fd.laterals.length);
              const foulImportRoadConstDepth = fd.roadConstConfig
                ? fd.roadConstConfig.depth ?? parseFloat((fd.roadConstConfig.roadLevel - fd.roadConstConfig.formationLevel).toFixed(3))
                : 0;
              const foulImportSeed = Date.now();
              const makeBlankFoulPipeRun = (roadConstDepth: number, seed: number): SewerPipeRun => ({
                id: `pr-${seed}`,
                ref: '', fromMH: '', toMH: '', length: 0, diameter: '',
                depthFrom: 0, depthTo: 0, averageDepth: 0, depthCategory: 0,
                runDepthCategory: 0, rockers: 0, pipeType: '', backfillType: '',
                roadConstDepth, concreteProtection: 0, junctionSize: '', junctions: 0,
              });
              const makeBlankFoulLateral = (seed: number): LateralConnection => ({
                id: `lat-${seed}`,
                ref: '', connectionType: 'direct_to_manhole',
                length: 0, pipeDiameter: '', pipeType: '', manholeDiameter: '',
                backdrop: 0, depth: 0, requiresFirstOff: false, rockers: 0,
                junctionOffRun: 0, demarcCoverLevel: 0, demarcInvertLevel: 0,
                demarcDepth: 0, demarcDepthCategory: 0, nme: 0, count1200: 0,
              });
              const paddedFoulPipeRuns = fd.pipeRuns.length >= foulImportedRowCount
                ? fd.pipeRuns
                : [...fd.pipeRuns, ...Array.from({ length: foulImportedRowCount - fd.pipeRuns.length }, (_, i) => makeBlankFoulPipeRun(foulImportRoadConstDepth, foulImportSeed + i))];
              const paddedFoulLaterals = fd.laterals.length >= foulImportedRowCount
                ? fd.laterals
                : [...fd.laterals, ...Array.from({ length: foulImportedRowCount - fd.laterals.length }, (_, i) => makeBlankFoulLateral(foulImportSeed + foulImportedRowCount + i))];

              updatedFoulSewers = {
                ...current.foulSewers,
                manholes: {
                  ...current.foulSewers.manholes,
                  table: fd.manholes,
                  config: {
                    ...current.foulSewers.manholes.config,
                    ...(fd.manholeScheduleConfig ? { manholeScheduleConfig: fd.manholeScheduleConfig } : {}),
                  },
                },
                pipeRuns: {
                  ...current.foulSewers.pipeRuns,
                  table: paddedFoulPipeRuns,
                  config: {
                    ...current.foulSewers.pipeRuns.config,
                    ...(fd.roadConstConfig ? { roadConstConfig: fd.roadConstConfig } : {}),
                    ...(fd.eoQtys ? { eoQtys: { ...current.foulSewers.pipeRuns.config.eoQtys, ...fd.eoQtys } } : {}),
                  },
                },
                lateralConnections: { ...current.foulSewers.lateralConnections, table: paddedFoulLaterals },
              };
              updatedPumpStation = {
                ...current.pumpStation,
                table: importedPumpStationRows,
                // Merge rather than replace — the imported sheet doesn't carry
                // Valve Chamber's Width/Length/Depth (Configure-modal-only), so
                // a full replace would silently wipe an already-configured value.
                ...(fd.pumpChamberConfig || fd.pumpStationSectionTitles ? {
                  config: {
                    ...(current.pumpStation.config as PumpChamberConfig),
                    ...(fd.pumpChamberConfig ?? {}),
                    ...(fd.pumpStationSectionTitles ? { sectionTitles: fd.pumpStationSectionTitles } : {}),
                  },
                } : {}),
              };
              updatedRisingMain = {
                ...current.risingMain,
                table: importedRisingMainRows,
              };
              importedSheets++;
              foulImported = true;
            }
          }

          if (stormImportResult.status === 'fulfilled' && stormImportResult.value) {
            const sd = stormImportResult.value.data;
            const hasStormBuildUp = sd.buildUp && Object.keys(sd.buildUp).length > 0;
            if (sd.manholes.length > 0 || sd.pipeRuns.length > 0 || sd.laterals.length > 0 || sd.gullies.length > 0 || hasStormBuildUp) {
              // Completely replace with imported data — do not merge with existing records.
              // eoQtys is the exception: it holds the Storm Summary tab's manual-input-only
              // quantities (EO Concrete Bed & Surround, EO Items, Removal of Redundant
              // Sewer), matched by description text out of the 'Storm Summary' sheet if
              // present (see parseStormSummarySheet) — merge rather than replace so a
              // workbook without that sheet, or with only some rows filled in, doesn't
              // wipe out previously-entered values. Same convention as
              // StormSewersTakeoff.tsx's handleUnifiedImport.
              // Build-up rows are keyed 'bc_*' (Box Culverts) / 'ca_*' (Cellular
              // Attenuation) / 'conc_*' (Conc Attenuation) / 'ps_*'|'pond_*'|'swale_*'|
              // 'hw_*' (Ponds/Swales) / 'wh_*' (Weholite Attenuation) — split by
              // prefix into the five dedicated top-level state fields.
              const importedStormBuildUp = sd.buildUp || {};
              const importedBoxCulvertRows: Record<string, FoulBuildUpRowData> = {};
              const importedCellularAttenuationRows: Record<string, FoulBuildUpRowData> = {};
              const importedConcAttenuationRows: Record<string, FoulBuildUpRowData> = {};
              const importedPondsSwalesRows: Record<string, FoulBuildUpRowData> = {};
              const importedWeholiteAttenuationRows: Record<string, FoulBuildUpRowData> = {};
              Object.entries(importedStormBuildUp).forEach(([id, row]) => {
                if (id.startsWith('bc_')) importedBoxCulvertRows[id] = row;
                else if (id.startsWith('ca_')) importedCellularAttenuationRows[id] = row;
                else if (id.startsWith('conc_')) importedConcAttenuationRows[id] = row;
                else if (id.startsWith('ps_') || id.startsWith('pond_') || id.startsWith('swale_') || id.startsWith('hw_')) importedPondsSwalesRows[id] = row;
                else if (id.startsWith('wh_')) importedWeholiteAttenuationRows[id] = row;
              });

              // The sheet's main-run, house-lateral, and gully sections are read
              // independently (not every run has a lateral or gully), so the
              // imported arrays commonly end up different lengths. Pad the shorter
              // ones so every take-off row renders with editable inputs on all
              // sides, matching StormSewersTakeoff.tsx's handleUnifiedImport.
              const stormImportedRowCount = Math.max(sd.pipeRuns.length, sd.laterals.length, sd.gullies.length);
              const stormImportRoadConstDepth = sd.roadConstConfig
                ? sd.roadConstConfig.depth ?? parseFloat((sd.roadConstConfig.roadLevel - sd.roadConstConfig.formationLevel).toFixed(3))
                : 0;
              const stormImportSeed = Date.now();
              const makeBlankStormPipeRun = (roadConstDepth: number, seed: number): StormSewerRun => ({
                id: `pr-${seed}`,
                ref: '', fromMH: '', toMH: '', length: 0, diameter: '',
                depthFrom: 0, depthTo: 0, averageDepth: 0, depthCategory: 0,
                runDepthCategory: 0, rockers: 0, pipeType: '', backfillType: '',
                roadConstDepth, concreteProtection: 0, junctionSize: '', junctions: 0,
              });
              const makeBlankStormLateral = (seed: number): LateralConnection => ({
                id: `lat-${seed}`,
                ref: '', connectionType: 'direct_to_manhole',
                length: 0, pipeDiameter: '', pipeType: '', manholeDiameter: '',
                backdrop: 0, depth: 0, requiresFirstOff: false, rockers: 0,
                junctionOffRun: 0, demarcCoverLevel: 0, demarcInvertLevel: 0,
                demarcDepth: 0, demarcDepthCategory: 0, nme: 0, count1200: 0,
              });
              const makeBlankGully = (seed: number): GullyConnection => ({
                id: `gu-${seed}`,
                gullyRef: '', location: '', gullyType: 'road',
                connectionType: 'junction_on_run', length: 0, pipeDiameter: '',
                gullyDepth: 0.9, connectionDepth: 0, depthCategory: 0, depthOffMainRun: 0,
                averageDepth: 0, avgDepthCategory: 0, gulleyIntoManhole: 0, junction150x150: 0,
                gulleys: 0, runRef: '', pipeType: '', concBS: 0,
              });
              const paddedStormPipeRuns = sd.pipeRuns.length >= stormImportedRowCount
                ? sd.pipeRuns
                : [...sd.pipeRuns, ...Array.from({ length: stormImportedRowCount - sd.pipeRuns.length }, (_, i) => makeBlankStormPipeRun(stormImportRoadConstDepth, stormImportSeed + i))];
              const paddedStormLaterals = sd.laterals.length >= stormImportedRowCount
                ? sd.laterals
                : [...sd.laterals, ...Array.from({ length: stormImportedRowCount - sd.laterals.length }, (_, i) => makeBlankStormLateral(stormImportSeed + stormImportedRowCount + i))];
              const paddedStormGullies = sd.gullies.length >= stormImportedRowCount
                ? sd.gullies
                : [...sd.gullies, ...Array.from({ length: stormImportedRowCount - sd.gullies.length }, (_, i) => makeBlankGully(stormImportSeed + stormImportedRowCount * 2 + i))];

              updatedStormSewers = {
                ...current.stormSewers,
                manholes: {
                  ...current.stormSewers.manholes,
                  table: sd.manholes,
                  config: {
                    ...current.stormSewers.manholes.config,
                    ...(sd.manholeScheduleConfig ? { manholeScheduleConfig: sd.manholeScheduleConfig } : {}),
                  },
                },
                pipeRuns: {
                  ...current.stormSewers.pipeRuns,
                  table: paddedStormPipeRuns,
                  config: {
                    ...current.stormSewers.pipeRuns.config,
                    ...(sd.roadConstConfig ? { roadConstConfig: sd.roadConstConfig } : {}),
                    ...(sd.eoQtys ? { eoQtys: { ...current.stormSewers.pipeRuns.config.eoQtys, ...sd.eoQtys } } : {}),
                  },
                },
                lateralConnections: { ...current.stormSewers.lateralConnections, table: paddedStormLaterals },
                gullyConnections: { ...current.stormSewers.gullyConnections, table: paddedStormGullies },
              };
              // Grow Box Culverts' sectionCount to cover the highest culvert n
              // present in the import, same reasoning as Cellular Attenuation above.
              const importedBoxCulvertMaxN = Object.keys(importedBoxCulvertRows).reduce((max, id) => {
                const m = id.match(/^bc_(\d+)_/);
                return m ? Math.max(max, Number(m[1])) : max;
              }, 0);
              updatedBoxCulverts = {
                ...current.boxCulverts,
                table: importedBoxCulvertRows,
                config: {
                  ...current.boxCulverts.config,
                  sectionCount: Math.min(
                    Math.max(current.boxCulverts.config?.sectionCount ?? DEFAULT_BOX_CULVERT_SECTIONS, importedBoxCulvertMaxN),
                    MAX_BOX_CULVERT_SECTIONS
                  ),
                  culvertConfigMm: { ...(current.boxCulverts.config?.culvertConfigMm ?? {}), ...(sd.boxCulvertsConfig?.culvertConfigMm ?? {}) },
                  culvertBackfillType: { ...(current.boxCulverts.config?.culvertBackfillType ?? {}), ...(sd.boxCulvertsConfig?.culvertBackfillType ?? {}) },
                },
              };
              // Grow Cellular Attenuation's sectionCount to cover the highest tank n
              // present in the import, same reasoning as pondSectionCount below.
              const importedCellularMaxN = Object.keys(importedCellularAttenuationRows).reduce((max, id) => {
                const m = id.match(/^ca_(\d+)_/);
                return m ? Math.max(max, Number(m[1])) : max;
              }, 0);
              updatedCellularAttenuation = {
                ...current.cellularAttenuation,
                table: importedCellularAttenuationRows,
                config: {
                  ...current.cellularAttenuation.config,
                  sectionCount: Math.min(
                    Math.max(current.cellularAttenuation.config?.sectionCount ?? DEFAULT_CELLULAR_SECTIONS, importedCellularMaxN),
                    MAX_CELLULAR_SECTIONS
                  ),
                  crateDims: { ...(current.cellularAttenuation.config?.crateDims ?? {}), ...(sd.cellularAttenuationConfig?.crateDims ?? {}) },
                },
              };
              // Grow Conc Attenuation's sectionCount to cover the highest tank n
              // present in the import, same reasoning as Cellular Attenuation above.
              const importedConcMaxN = Object.keys(importedConcAttenuationRows).reduce((max, id) => {
                const m = id.match(/^conc_(\d+)_/);
                return m ? Math.max(max, Number(m[1])) : max;
              }, 0);
              updatedConcAttenuation = {
                ...current.concAttenuation,
                table: importedConcAttenuationRows,
                config: {
                  ...current.concAttenuation.config,
                  sectionCount: Math.min(
                    Math.max(current.concAttenuation.config?.sectionCount ?? DEFAULT_CONC_SECTIONS, importedConcMaxN),
                    MAX_CONC_SECTIONS
                  ),
                  concConfigPct: { ...(current.concAttenuation.config?.concConfigPct ?? {}), ...(sd.concAttenuationConfig?.concConfigPct ?? {}) },
                  drawingRefs: { ...(current.concAttenuation.config?.drawingRefs ?? {}), ...(sd.concAttenuationConfig?.drawingRefs ?? {}) },
                },
              };
              // Grow pondSectionCount to cover the highest POND n present in the
              // import so any pond beyond the current section count renders
              // instead of sitting hidden in .table with no visible panel.
              const importedPondMaxN = Object.keys(importedPondsSwalesRows).reduce((max, id) => {
                const m = id.match(/^pond_(\d+)_/);
                return m ? Math.max(max, Number(m[1])) : max;
              }, 0);
              // Same growth logic for Swale sections.
              const importedSwaleMaxN = Object.keys(importedPondsSwalesRows).reduce((max, id) => {
                const m = id.match(/^swale_(\d+)_/);
                return m ? Math.max(max, Number(m[1])) : max;
              }, 0);
              updatedPondsSwales = {
                ...current.pondsSwales,
                table: importedPondsSwalesRows,
                config: {
                  ...current.pondsSwales.config,
                  pondSectionCount: Math.min(
                    Math.max(current.pondsSwales.config?.pondSectionCount ?? DEFAULT_POND_SECTIONS, importedPondMaxN),
                    MAX_POND_SECTIONS
                  ),
                  pondTopsoilThicknessMm: { ...(current.pondsSwales.config?.pondTopsoilThicknessMm ?? {}), ...(sd.pondsSwalesConfig?.pondTopsoilThicknessMm ?? {}) },
                  swaleSectionCount: Math.min(
                    Math.max(current.pondsSwales.config?.swaleSectionCount ?? DEFAULT_SWALE_SECTIONS, importedSwaleMaxN),
                    MAX_SWALE_SECTIONS
                  ),
                  swaleTopsoilThicknessMm: { ...(current.pondsSwales.config?.swaleTopsoilThicknessMm ?? {}), ...(sd.pondsSwalesConfig?.swaleTopsoilThicknessMm ?? {}) },
                  // Titles come from the sheet, replacing previously typed ones (same as the imported table).
                  sectionTitles: sd.pondsSwalesConfig?.sectionTitles ?? current.pondsSwales.config?.sectionTitles ?? {},
                },
              };
              // Grow Weholite Attenuation's sectionCount to cover the highest tank n
              // present in the import, same reasoning as Cellular Attenuation above.
              const importedWeholiteMaxN = Object.keys(importedWeholiteAttenuationRows).reduce((max, id) => {
                const m = id.match(/^wh_(\d+)_/);
                return m ? Math.max(max, Number(m[1])) : max;
              }, 0);
              updatedWeholiteAttenuation = {
                ...current.weholiteAttenuation,
                table: importedWeholiteAttenuationRows,
                config: {
                  ...current.weholiteAttenuation.config,
                  sectionCount: Math.min(
                    Math.max(current.weholiteAttenuation.config?.sectionCount ?? DEFAULT_WEHOLITE_SECTIONS, importedWeholiteMaxN),
                    MAX_WEHOLITE_SECTIONS
                  ),
                  weholiteConfigPct: { ...(current.weholiteAttenuation.config?.weholiteConfigPct ?? {}), ...(sd.weholiteAttenuationConfig?.weholiteConfigPct ?? {}) },
                },
              };
              importedSheets++;
              stormImported = true;
            }
          }

          if (posImportResult.status === 'fulfilled' && posImportResult.value) {
            const imported = posImportResult.value;
            if (Object.keys(imported.table).length > 0) {
              importedSheets++;
              posImported = true;
            }
          }

          if (compoundImportResult.status === 'fulfilled' && compoundImportResult.value) {
            const imported = compoundImportResult.value;
            if (Object.keys(imported.table).length > 0) {
              importedSheets++;
              compoundImported = true;
            }
          }

          if (substationBaseImportResult.status === 'fulfilled' && substationBaseImportResult.value) {
            const imported = substationBaseImportResult.value;
            if (Object.keys(imported.table).length > 0) {
              importedSheets++;
              substationBaseImported = true;
            }
          }

          if (spoilReconciliationImportResult.status === 'fulfilled' && spoilReconciliationImportResult.value) {
            const imported = spoilReconciliationImportResult.value;
            if (Object.keys(imported).length > 0) {
              importedSheets++;
              spoilReconciliationImported = true;
            }
          }

          // Prelims — read "Prelims" sheet inline (col 2 = description, col 7 = qty),
          // matching against PRELIMS_MAPPING exactly as PreliminaryItems.tsx does.
          if (true) {
            const preliSheetName = workbook.SheetNames.find((n: string) =>
              n.trim().toLowerCase() === 'prelims'
            );
            if (preliSheetName) {
              const preliWs = workbook.Sheets[preliSheetName];
              const preliRows = XLSX.utils.sheet_to_json(preliWs, {
                header: 1,
                raw: true,
                blankrows: true,
                defval: null,
              }) as unknown[][];

              // Some descriptions (e.g. "Erect heras fencing on feet", "Dismantle
              // heras fencing upon completion") repeat verbatim under multiple parent
              // groups in the Prelims sheet, each with its own quantity. Queue up each
              // description's quantities in the top-to-bottom order they appear in the
              // sheet, then consume one per PRELIMS_MAPPING occurrence below — the
              // cat/grp/item nested loop walks in the same document order as the sheet,
              // so repeated items get assigned to their correct parent group in order
              // instead of collapsing into a single shared value.
              const preliQtyQueueByDescription = new Map<string, number[]>();
              for (const row of preliRows) {
                const desc = (row as unknown[])[2];
                const qty  = (row as unknown[])[7];
                if (
                  typeof desc === 'string' &&
                  desc.trim() &&
                  qty != null &&
                  !isNaN(Number(qty)) &&
                  Number(qty) > 0
                ) {
                  const key = desc.trim().toLowerCase();
                  const queue = preliQtyQueueByDescription.get(key);
                  if (queue) queue.push(Number(qty));
                  else preliQtyQueueByDescription.set(key, [Number(qty)]);
                }
              }

              if (preliQtyQueueByDescription.size > 0) {
                const importedPrelimItems: PrelimsItemRecord[] = [];
                for (const cat of PRELIMS_MAPPING) {
                  for (const mapItem of cat.items ?? []) {
                    const importedQty = preliQtyQueueByDescription.get(mapItem.description.trim().toLowerCase())?.shift();
                    if (importedQty === undefined) continue;
                    const deterministicId = `prelim-${cat.id}-${mapItem.description.replace(/[^a-zA-Z0-9]/g, '-')}`;
                    importedPrelimItems.push({
                      id: deterministicId,
                      description: mapItem.description,
                      unit: mapItem.unit,
                      code: mapItem.code,
                      category: cat.label,
                      group: undefined,
                      quantity: importedQty,
                      rate: 0, // rates applied from item library when page loads
                      amount: 0,
                    });
                  }
                  for (const grp of cat.groups) {
                    for (const mapItem of grp.items) {
                      const importedQty = preliQtyQueueByDescription.get(mapItem.description.trim().toLowerCase())?.shift();
                      if (importedQty === undefined) continue;
                      const deterministicId = `prelim-${cat.id}-${grp.id}-${mapItem.description.replace(/[^a-zA-Z0-9]/g, '-')}`;
                      importedPrelimItems.push({
                        id: deterministicId,
                        description: mapItem.description,
                        unit: mapItem.unit,
                        code: mapItem.code,
                        category: cat.label,
                        group: grp.label,
                        quantity: importedQty,
                        rate: 0, // rates applied from item library when page loads
                        amount: 0,
                      });
                    }
                  }
                }

                if (importedPrelimItems.length > 0) {
                  updatedPreliminaryItems = {
                    ...current.preliminaryItems,
                    preliminaryItems: importedPrelimItems,
                  };
                  importedSheets++;
                  prelimsImported = true;
                }
              }
            }
          }

          // ── Main Services Trenching import ─────────────────────────────────
          if (true && importedMainServiceEntries.length > 0) {
            // Use just-imported roads for ID resolution; fall back to existing state
            const resolvedRoads = importedCarriagewayFootways.length > 0
              ? importedCarriagewayFootways
              : (current.roads?.carriagewayFootways.table ?? []);

            const quantities: Record<string, Record<string, number>> = {};
            importedMainServiceEntries.forEach(({ itemId, roadIndex, roadHeaderName, quantity }) => {
              const byName = resolvedRoads.find(
                (r) => r.roadName.trim().toLowerCase() === roadHeaderName.toLowerCase(),
              );
              const road = byName ?? resolvedRoads[roadIndex];
              if (!road) return;
              if (!quantities[itemId]) quantities[itemId] = {};
              quantities[itemId][road.id] = quantity;
            });

            if (Object.keys(quantities).length > 0) {
              // Completely replace main service trenching quantities with imported data
              updatedMainServices = {
                ...updatedMainServices,
                table: quantities,
              };
              if (!mainServicesImported) {
                importedSheets++;
                mainServicesImported = true;
              }
            }
          }

          // Update the tender context with all imported data
            // Build the complete updated state and save directly to storage
            // This avoids the race condition where saveTender() would use stale state
            const importedPOS = posImportResult.status === 'fulfilled' ? posImportResult.value : null;
            const updatedPOS = posImported && importedPOS
              ? { ...current.pos, table: importedPOS.table, config: { ...current.pos?.config, sectionCount: importedPOS.config.sectionCount, rowThicknessMm: importedPOS.config.rowThicknessMm, sectionTitles: importedPOS.config.sectionTitles } }
              : current.pos;

            const importedCompound = compoundImportResult.status === 'fulfilled' ? compoundImportResult.value : null;
            const updatedCompoundTable = compoundImported && importedCompound
              ? importedCompound.table
              : (current.compound?.table ?? {});
            const updatedCompoundConfig = compoundImported && importedCompound
              ? importedCompound.config
              : (current.compound?.config ?? {});
            const updatedCompound = compoundImported
              ? { config: updatedCompoundConfig, table: updatedCompoundTable }
              : current.compound;

            const importedSubstationBase = substationBaseImportResult.status === 'fulfilled' ? substationBaseImportResult.value : null;
            const updatedSubstationTable = substationBaseImported && importedSubstationBase
              ? importedSubstationBase.table
              : (current.substation?.table ?? {});
            const updatedSubstationConfig = substationBaseImported && importedSubstationBase
              ? importedSubstationBase.config
              : (current.substation?.config ?? {});
            const updatedSubstation = substationBaseImported
              ? { ...current.substation, table: updatedSubstationTable, config: updatedSubstationConfig }
              : current.substation;

            const importedSpoilReconciliation = spoilReconciliationImportResult.status === 'fulfilled' ? spoilReconciliationImportResult.value : null;
            const updatedSpoilReconciliation = spoilReconciliationImported && importedSpoilReconciliation
              ? { config: { ...(current.spoilReconciliation?.config ?? {}), ...importedSpoilReconciliation } as SpoilReconciliationData }
              : current.spoilReconciliation;


            const updates: Partial<TenderState> = {
              roads: updatedRoads,
              foulSewers: updatedFoulSewers,
              pumpStation: updatedPumpStation,
              risingMain: updatedRisingMain,
              ...(stormImported ? {
                stormSewers: updatedStormSewers,
                boxCulverts: updatedBoxCulverts,
                cellularAttenuation: updatedCellularAttenuation,
                concAttenuation: updatedConcAttenuation,
                pondsSwales: updatedPondsSwales,
                weholiteAttenuation: updatedWeholiteAttenuation,
              } : {}),
              preliminaryItems: updatedPreliminaryItems,
              ...(mainServicesImported ? { mainServices: updatedMainServices } : {}),
              ...(posImported ? { pos: updatedPOS } : {}),
              ...(compoundImported ? { compound: updatedCompound } : {}),
              ...(substationBaseImported ? { substation: updatedSubstation } : {}),
              ...(spoilReconciliationImported ? { spoilReconciliation: updatedSpoilReconciliation } : {}),
            };

            const pageChanges: Array<{ page: string; count: number }> = [];
            if (roadsImported) {
              const rd = (roadsImportResult as PromiseFulfilledResult<any>).value.data;
              pageChanges.push({ page: "roads", count: rd.roadTypes.length + rd.carriagewayFootways.length + rd.cutFillSections.length });
            }
            if (foulImported) {
              const fd = (foulImportResult as PromiseFulfilledResult<any>).value.data;
              pageChanges.push({ page: "foul-sewers", count: fd.manholes.length + fd.pipeRuns.length + fd.laterals.length });
            }
            if (prelimsImported) {
              pageChanges.push({ page: "preliminary-items", count: updatedPreliminaryItems.preliminaryItems?.length ?? 0 });
            }

            resolve({
              updates,
              importedSheets,
              pageChanges,
              hasNavigationPriorityData: roadsImported || foulImported || prelimsImported,
            });
          } catch (err) {
            reject(err instanceof Error ? err : new Error(String(err)));
          }
        };
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsArrayBuffer(file);
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    })();
  });
}
